import { Listing, School } from 'types/collections';

import { showToast } from '@components/ToastContainer';

import { formatAddress } from '@utils/helpers';

// Types
export interface FieldErrors {
  intro: boolean;
  role: boolean;
  rules: boolean;
  supplies: boolean;
  photo: boolean;
}

export interface ListingData {
  intro: string;
  role: string;
  rules: string;
  supplies: string;
}

/**
 * Get field labels for validation messages
 */
export const getFieldLabels = (t: (key: string) => string) => ({
  intro: t('introduction'),
  role: t('schoolListing.function'),
  rules: t('schoolListing.regulation'),
  supplies: t('schoolListing.supplies'),
});

/**
 * Validate form data and return error states
 */
export const validateFormData = (
  listingData: ListingData,
  photoPath?: string | null,
): FieldErrors => {
  return {
    intro: !listingData.intro.trim(),
    role: !listingData.role.trim(),
    rules: !listingData.rules.trim(),
    supplies: !listingData.supplies.trim(),
    photo: !photoPath,
  };
};

/**
 * Show toast messages for validation errors
 */
export const showValidationErrors = (errors: FieldErrors, t: (key: string) => string): void => {
  const fieldLabels = getFieldLabels(t);

  Object.entries(errors).forEach(([field, hasError]) => {
    if (hasError) {
      showToast(
        'error',
        fieldLabels[field as keyof typeof fieldLabels],
        t('schoolListing.required'),
      );
    }
  });
};

/**
 * Complete validation function that returns errors and shows toasts
 */
export const validateListingForm = (
  listingData: ListingData,
  t: (key: string) => string,
  photoPath?: string | null,
): { isValid: boolean; errors: FieldErrors } => {
  const errors = validateFormData(listingData, photoPath);
  const isValid = !Object.values(errors).some(Boolean);

  if (!isValid) {
    showValidationErrors(errors, t);
  }

  return { isValid, errors };
};

/**
 * Checks if a listing is published
 */
export const isListingPublished = (listing: Listing | null): boolean => {
  return !!(listing && listing.listing_completed);
};

/**
 * Determines if edit mode should be shown
 */
export const shouldShowEditMode = (
  listing: Listing | null,
  isPublished: boolean,
  isEditMode: boolean,
): boolean => {
  // Show edit mode if:
  // - Listing doesn't exist (new listing)
  // - Listing exists but is not published
  // - User clicked edit on a published listing
  return !listing || !isPublished || isEditMode;
};

/**
 * Initializes listing data with default values
 */
export const initializeListingData = (listing: Listing | null): ListingData => {
  return {
    intro: listing?.intro || '',
    role: listing?.role || '',
    rules: listing?.rules || '',
    supplies: listing?.supplies || '',
  };
};

/**
 * Filters listing update to include only allowed fields
 */
export const filterListingUpdate = (updatedListing: Partial<Listing>) => {
  return Object.fromEntries(
    Object.entries(updatedListing).filter(
      ([key, value]) =>
        ['intro', 'role', 'rules', 'supplies'].includes(key) && typeof value === 'string',
    ),
  ) as Partial<ListingData>;
};

/**
 * Gets school name from school profile
 */
export const getSchoolName = (schoolProfile: School | null): string => {
  return schoolProfile?.primary_education?.vestigingsnaam || 'School Name';
};

/**
 * Gets formatted school address from school profile
 */
export const getSchoolAddress = (schoolProfile: School | null): string | null => {
  if (!schoolProfile?.primary_education) return null;
  return formatAddress(schoolProfile.primary_education);
};
